import { type RefObject, useEffect } from 'react';
import { nextZoomStep } from '../../core/view';
import { useEditorStore } from '../../state/editorStore';
import { clientToStage } from '../pointer';

/**
 * The zoom tool: click to zoom in one step, Alt-click to zoom out, anchored
 * at the click point so the thing you clicked stays under the cursor.
 *
 * Deliberately small — drag-to-zoom-a-region is out of scope (design doc
 * §3.5), and wheel-zoom already lives on the stage itself.
 */
export function useZoomTool(stageRef: RefObject<HTMLDivElement | null>): void {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const onPointerDown = (event: PointerEvent) => {
      // Honour a pan gesture (space or middle button) claimed in capture.
      if (event.defaultPrevented || event.button !== 0) return;
      const store = useEditorStore.getState();
      if (store.activeTool !== 'zoom') return;
      const anchor = clientToStage(event.clientX, event.clientY, stage);
      const direction = event.altKey ? -1 : 1;
      store.zoomTo(nextZoomStep(store.viewport.zoom, direction), anchor);
      event.preventDefault();
    };

    stage.addEventListener('pointerdown', onPointerDown);
    return () => stage.removeEventListener('pointerdown', onPointerDown);
  }, [stageRef]);
}
